import * as React from 'react';
import { RiseOutlined, FallOutlined } from '@ant-design/icons';
import humanizeUtil from '@utils/humanizeUtil';
import { getMetricMetaByKey, getMetricUnitByKey } from '@utils/metrics';
import Trend from './Trend';
import { isMetricChangeGood } from './utils/utils';
import {
  StyledTitle,
  StyledValue,
  ChartWrapper,
  PercentBadge,
  ContentRow,
  ValueSection,
  EmptyStateContainer,
  UnitText,
  BadgeContainer,
  TitleRow,
} from './styled';
import type { TrendProps } from './Trend';

export interface MetricTrendProps extends Omit<TrendProps, 'metricKey'> {
  metricKey?: string;
  unit?: string;
  total?: number | null;
  placeholder?: React.ReactNode;
  chartStyle?: React.CSSProperties;
  dodData?: any[];
  percentageChange?: number | null;
  isEmpty?: boolean;
}

const MetricTrend = (props: MetricTrendProps) => {
  const {
    metricKey,
    total,
    data,
    dodData = [],
    percentageChange,
    unit: propsUnit,
    placeholder,
    chartStyle,
    isEmpty = false,
  } = props;

  const unit = propsUnit ?? (metricKey ? getMetricUnitByKey({ metricKey }) : undefined);
  const metricMeta = metricKey ? getMetricMetaByKey({ metricKey }) : undefined;

  // Check if metric supports percentiles
  const supportsPercentiles = metricMeta?.supportsPercentiles ?? false;

  // Arrow direction is based on whether value went up or down
  const isIncreasing =
    percentageChange !== null && percentageChange !== undefined ? percentageChange > 0 : null;

  // Color is based on whether the change is good or bad (considering the metric's reverse property)
  const isGoodChange = isMetricChangeGood(percentageChange, metricMeta);

  const percentageText =
    percentageChange !== null && percentageChange !== undefined && percentageChange !== 0
      ? `${Math.abs(percentageChange).toFixed(1)}%`
      : null;

  const hasBadge = !isEmpty && percentageText;

  // Use humanizeUtil for better number formatting
  const formattedValue = React.useMemo(() => {
    if (isEmpty || total === null || total === undefined) {
      return { humanizedNumber: 'No data', humanizedUnit: '' };
    }
    if (metricKey) {
      return humanizeUtil(total, metricKey, unit);
    }
    return { humanizedNumber: String(total), humanizedUnit: unit || '' };
  }, [isEmpty, total, metricKey, unit]);

  return (
    <ChartWrapper>
      <TitleRow>
        <StyledTitle data-metric-key={metricKey}>
          <div>{metricMeta?.displayName}</div>
        </StyledTitle>
        <ValueSection $centered={!hasBadge}>
          <StyledValue $isEmpty={isEmpty}>
            {formattedValue.humanizedNumber}{' '}
            {formattedValue.humanizedUnit && <UnitText>{formattedValue.humanizedUnit}</UnitText>}
          </StyledValue>
          {supportsPercentiles && (
            <div
              style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px', fontWeight: 'bold' }}
            >
              (P90)
            </div>
          )}
          {hasBadge && (
            <BadgeContainer>
              <PercentBadge positive={Boolean(isGoodChange)}>
                {isIncreasing ? <RiseOutlined /> : <FallOutlined />}
                {percentageText}
              </PercentBadge>
            </BadgeContainer>
          )}
        </ValueSection>
      </TitleRow>
      <ContentRow>
        {metricKey && !isEmpty ? (
          <Trend metricKey={metricKey} data={data} dodData={dodData} style={chartStyle} />
        ) : isEmpty ? (
          <EmptyStateContainer>No trend data available</EmptyStateContainer>
        ) : (
          placeholder
        )}
      </ContentRow>
    </ChartWrapper>
  );
};

export default React.memo(MetricTrend);
